import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { DataGrid, GridToolbarContainer, GridToolbarExport } from '@mui/x-data-grid';
import useBreakpoint from '../themes/hooks/useBreakpoint';

const BREAKPOINT_ORDER = ['xs', 'sm', 'md', 'lg', 'xl'];

/**
 * Reusable Server-Side DataGrid Component
 * 
 * @param {Object} props
 * @param {Array} props.columns - Column definitions
 * @param {Function} props.fetchData - Function to fetch data: (page, pageSize, filters, sort) => Promise
 * @param {Number} props.initialPageSize - Initial page size (default: 10)
 * @param {Array} props.rowsPerPageOptions - Page size options (default: [10, 25, 50, 100])
 * @param {Function} props.getRowId - Function to get row ID
 * @param {Function} props.onRowClick - Optional row click handler
 * @param {Boolean} props.enableExport - Show export button (default: true)
 * @param {Object} props.additionalProps - Any additional DataGrid props
 */
export default function ServerDataGrid({
    columns,
    fetchData,
    initialPageSize = 10,
    rowsPerPageOptions = [10, 25, 50, 100],
    getRowId,
    onRowClick,
    enableExport = true,
    additionalProps = {},
    /** fill — when true the grid stretches to fill its parent flex container
     *  instead of using a fixed 600px height. Requires the parent to have a
     *  defined height (e.g. PageContent fill). */
    fill = false,
    /** containerSx — additional styles merged onto the outer wrapper div */
    containerSx = {},
    /** initialSortModel — array describing initial sort e.g. [{ field: 'createdAt', sort: 'desc' }] */
    initialSortModel = [],
    /** clientSort — { field, order } — sorts each fetched page's rows on the client side.
     *  Does NOT affect which records are on which page (server pagination is unchanged).
     *  Only active when the user has not clicked a column header to sort. */
    clientSort = null,
}) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: initialPageSize,
    });
    const pendingRequestsRef = useRef(new Map());
    const [rowCount, setRowCount] = useState(0);
    const [filterModel, setFilterModel] = useState({ items: [] });
    const filterModelRef = useRef({ items: [] });
    const [sortModel, setSortModel] = useState(initialSortModel);
    const [fetchTrigger, setFetchTrigger] = useState(0);
    const debounceTimerRef = useRef(null);
    const breakpoint = useBreakpoint();
    const currentIdx = BREAKPOINT_ORDER.indexOf(breakpoint);

    // Process minVisible — hide columns below their minimum breakpoint
    const processedColumns = useMemo(() => {
        return columns.map(({ minVisible, ...col }) => {
            if (minVisible) {
                const minIdx = BREAKPOINT_ORDER.indexOf(minVisible);
                return { ...col, hide: currentIdx < minIdx };
            }
            return col;
        });
    }, [columns, currentIdx]);
    useEffect(() => {
        filterModelRef.current = filterModel;
    }, [filterModel]);
    // Debounce filter changes to avoid excessive API calls
    useEffect(() => {
        // Clear any existing timer
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        const isReset = filterModel.items.length === 0;

        // Also treat as reset when all items have empty values (user cleared the text box)
        const allValuesCleared = !isReset && filterModel.items.every(item => {
            const isEmptyOperator = item.operatorValue === 'isEmpty' || item.operatorValue === 'isNotEmpty';
            return !isEmptyOperator && (item.value === undefined || item.value === null || item.value === '');
        });

        // Check if all filters are complete (have all required fields)
        const allFiltersComplete = !isReset && !allValuesCleared && filterModel.items.every(item => {
            const isEmptyOperator = item.operatorValue === 'isEmpty' || item.operatorValue === 'isNotEmpty';
            return isEmptyOperator ||
                   (item.operatorValue && item.value !== undefined && item.value !== null && item.value !== '');
        });

        if (isReset || allValuesCleared || allFiltersComplete) {
            const delay = (isReset || allValuesCleared) ? 0 : 500;
            debounceTimerRef.current = setTimeout(() => {
                setPaginationModel(prev => ({ ...prev, page: 0 }));
                setFetchTrigger(prev => prev + 1);
                debounceTimerRef.current = null;
            }, delay);
        }

        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, [filterModel]);

    // Fetch data whenever pagination, sort, or fetchTrigger changes
    useEffect(() => {
    let isCancelled = false;
    const loadData = async () => {
        setLoading(true);

        try {
            // Build filters object
            const filters = {};

            filterModelRef.current.items.forEach(item => {
                const field = item.columnField || item.field;
                const operator = item.operatorValue || item.operator || 'contains';
                if (!field) return;

                const isEmptyOperator =operator === 'isEmpty' ||operator === 'isNotEmpty';

                if (isEmptyOperator ||
                    (item.value !== undefined &&item.value !== null &&item.value !== '')) {
                    filters[field] = {
                        operator,
                        value: item.value
                    };
                }
            });

            // Build sort object
            const sort = sortModel.length > 0
                ? {
                    field: sortModel[0].field,
                    order: sortModel[0].sort
                }
                : null;

            const requestPayload = {
                page: paginationModel.page + 1,
                pageSize: paginationModel.pageSize,
                filters,
                sort
            };
            const requestKey = JSON.stringify(requestPayload);

            console.log('ServerDataGrid fetching:', requestPayload);
            let result;
            // If the same request is already running, reuse it
            if (pendingRequestsRef.current.has(requestKey)) {
                result = await pendingRequestsRef.current.get(requestKey);
            } else {
                const requestPromise = fetchData(
                    paginationModel.page + 1,
                    paginationModel.pageSize,
                    filters,
                    sort
                );

                pendingRequestsRef.current.set(requestKey, requestPromise);
                try {
                    result = await requestPromise;
                } finally {
                    pendingRequestsRef.current.delete(requestKey);
                }
            }
            if (isCancelled) return;

            const applyClientSort = (data) => {
                if (!clientSort || sortModel.length > 0) return data;
                const { field, order = 'asc' } = clientSort;
                return [...data].sort((a, b) => {
                    const av = (a[field] ?? '').toString().toLowerCase();
                    const bv = (b[field] ?? '').toString().toLowerCase();
                    return order === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
                });
            };

            if (result?.data) {
                setRows(applyClientSort(result.data));
                setRowCount(result.pagination?.total || result.data.length);
            } else if (Array.isArray(result)) {
                setRows(applyClientSort(result));
                setRowCount(result.length);
            } else {
                setRows([]);
                setRowCount(0);
            }
        } catch (error) {
            if (!isCancelled) {
                console.error('ServerDataGrid fetch error:', error);
                setRows([]);
                setRowCount(0);
            }
        } finally {
            if (!isCancelled) {
                setLoading(false);
            }
        }
    };

    loadData();

    return () => {
        isCancelled = true;
    };
}, [
    paginationModel.page,
    paginationModel.pageSize,
    sortModel,
    fetchData,
    fetchTrigger,
]);

    // Handlers
    const handlePageChange = useCallback((newPage) => {
        console.log('Page change:', newPage);
        setPaginationModel(prev => ({ ...prev, page: newPage }));
    }, []);

    const handlePageSizeChange = useCallback((newPageSize) => {
        console.log('Page size change:', newPageSize);
        setPaginationModel({ page: 0, pageSize: newPageSize });
    }, []);

    const handleFilterChange = useCallback((newFilterModel) => {
        console.log('Filter change:', newFilterModel);
        setFilterModel(newFilterModel);
        // Page reset is now handled by debounced effect
    }, []);

    const handleSortChange = useCallback((newSortModel) => {
        console.log('Sort change:', newSortModel);
        setSortModel(newSortModel);
    }, []);

    // Custom Toolbar
    function CustomToolbar() {
        return (
            <GridToolbarContainer>
                {enableExport && <GridToolbarExport />}
            </GridToolbarContainer>
        );
    }

    return (
        <div style={{ height: fill ? '100%' : 600, width: '100%', marginTop: fill ? 0 : '16px', ...containerSx }}>
            <DataGrid
                rows={rows}
                columns={processedColumns}
                loading={loading}
                getRowId={getRowId}
                
                // Server-side pagination
                paginationMode="server"
                rowCount={rowCount}
                page={paginationModel.page}
                pageSize={paginationModel.pageSize}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                rowsPerPageOptions={rowsPerPageOptions}
                
                // Server-side filtering
                filterMode="server"
                filterModel={filterModel}
                onFilterModelChange={handleFilterChange}
                
                // Server-side sorting
                sortingMode="server"
                sortModel={sortModel}
                onSortModelChange={handleSortChange}
                
                // Optional row click handler
                onRowClick={(params) => onRowClick && onRowClick(params.row)}
                
                // Toolbar
                components={{
                    Toolbar: CustomToolbar,
                }}
                
                // Other props
                disableSelectionOnClick
                {...additionalProps}
            />
        </div>
    );
}
